<template>
	<div
		v-if="sizes && sizes.length > 0"
		class="storage-selector"
		ref="storage-selector"
	>
		<div v-if="sizes.length > 1">
			<TnParagraph
				class="storage-text"
				size="l"
				responsive
				bold
				v-if="product.category === 'Tilbehør' || product.category === 'Smartklokker'"
			>
				{{ sizeText }}
			</TnParagraph>
			<TnParagraph
				class="storage-text"
				size="l"
				responsive
				bold
				v-else
			>
				{{ sizeText }}
			</TnParagraph>
			<ECommerceStorageSelectorItem
				v-for="(size, idx) in sizes"
				:key="`size-${idx}`"
				class="item"
				@click="sizeSelected(size)"
				:model-value="sizeFromPath"
				:class="{ disabled: sizes.length == 1 }"
				:to="size.href"
				:disabled="isDisabled(size)"
				:active="eCommerceProduct.memory === size.memory"
				>{{ size.memory }}
			</ECommerceStorageSelectorItem>
		</div>
		<div v-else-if="sizes.length === 1">
			<TnParagraph
				class="storage-text"
				size="l"
				bold
				responsive
				v-if="product.category === 'Tilbehør' || product.category === 'Smartklokker'"
			>
				{{ sizeText }}
			</TnParagraph>
			<TnParagraph
				size="l"
				bold
				responsive
				class="storage-text"
				v-else
			>
				{{ sizeText }}
			</TnParagraph>
			<ECommerceStorageSelectorItem
				v-for="(size, idx) in sizes"
				:key="`size-${idx}`"
				class="item"
				active
				>{{ size.memory }}
			</ECommerceStorageSelectorItem>
		</div>
		<div
			v-if="infoText"
			class="info-text-container"
		>
			<TnParagraph
				size="l"
				v-if="content?.modalEnabled"
				class="info-text"
				@click="toggleModal"
				responsive
			>
				<TnIcon name="QuestionCircleOutline" />

				{{ infoText }}
			</TnParagraph>
		</div>
		<StorageModal
			:heading="content.modalHeading"
			:freeText="content.modalFreeText"
			v-model="modal"
		/>
	</div>
</template>

<script>
import GizmoComponentMixin from "~/mixins/platform/GizmoComponentMixin.js";
import ECommerceProductMixin from "~/components/ecommerce/ECommerceProductMixin/ECommerceProductMixin.js";

import safeEcommerceUrl from "~/helpers/ecommerce/safeEcommerceUrl";

import { mapState } from "vuex";
import StorageModal from "./StorageModal.vue";

import bytes from "bytes";

import { mapActions } from "pinia";
import { usePageStore } from "~/pinia/platform/page.ts";

const getSortableValue = (str) => {
	const matches = str.match(/(\d+)\s?(\w*)/);
	const value = matches[1];
	const unit = matches[2];

	if (value && unit) {
		return bytes(str);
	} else {
		if (value) return value; // Probably a physical size, such as inches
		return -1;
	}
};

export default defineNuxtComponent({
	name: "EcommerceStorageSelector",
	mixins: [GizmoComponentMixin, ECommerceProductMixin],

	data() {
		return {
			modal: false,
			size: "",
		};
	},
	components: {
		StorageModal,
	},

	computed: {
		...mapState("ecommerce", ["product", "categoriesThatRequireStock"]),
		variants() {
			if (this.eCommerceProduct?.variants?.length > 0)
				return this.eCommerceProduct?.variants.filter((v) => v.colorCode === this.eCommerceProduct.colorCode);
			else return [this.eCommerceProduct];
		},

		sizeText() {
			return this.content.heading;
		},

		infoText() {
			return this.content.infoText;
		},

		sizes() {
			if (this.eCommerceProduct?.variants?.length > 0 && this.eCommerceProduct?.memory) {
				const variantsCopy = JSON.parse(JSON.stringify(this.eCommerceProduct?.variants.filter((x) => x.memory)));

				if (variantsCopy.length > 0) {
					variantsCopy.sort((a, b) => {
						const aInt = getSortableValue(a?.memory);
						const bInt = getSortableValue(b?.memory);

						if (aInt > bInt) return 1;
						else if (aInt < bInt) return -1;
						return 0;
					});
				}

				const uniqueVariants = [];

				variantsCopy.forEach((vc) => {
					if (uniqueVariants.find((uv) => uv.memory === vc.memory))
						// This memory option has already been resolved
						return;
					const matchingVariants = variantsCopy.filter((v) => v.memory === vc.memory);
					const memoryMatch = matchingVariants.find(
						(mv) =>
							(mv.condition === this.eCommerceProduct?.condition &&
								mv.colorCode === this.eCommerceProduct?.colorCode) ||
							mv.colorCode === this.eCommerceProduct?.colorCode,
					);
					if (memoryMatch) uniqueVariants.push(memoryMatch);
					else uniqueVariants.push(matchingVariants[0]);
				});

				return uniqueVariants.map((variant) => {
					return {
						...variant,
						href: safeEcommerceUrl(
							this.eCommerceProduct?.category,
							this.eCommerceProduct?.brand,
							variant.title,
							variant.condition,
						),
					};
				});
			} else if (this.eCommerceProduct?.memory) {
				return [this.eCommerceProduct];
			} else {
				return [];
			}
		},
		requiresStock() {
			return this.categoriesThatRequireStock.includes(this.eCommerceProduct?.category);
		},
		sizeFromPath() {
			return this.eCommerceProduct?.memory;
		},
	},

	methods: {
		...mapActions(usePageStore, ["loadPage"]),
		toggleModal() {
			this.modal = !this.modal;

			if (this.modal && this.postalCode && !this.isDeliveryWindowOpen) {
				this.checkPorterBuddyDeliveryAvailability(this.postalCode);
			}
		},
		async sizeSelected(size) {
			this.$store.commit("ecommerce/setProduct", {
				...this.$store.state.ecommerce.product,
				...size,
				loading: true,
			}); // Fake update to make UI react instantly

			useAnalytics().resetProducts();

			if (import.meta.client) {
				history.replaceState(null, "", size.href + window.location.hash);
			}

			await this.loadPage(size.href);

			useAnalytics().page.productLoaded({
				products: useAnalytics().getProducts(),
			});

			if (import.meta.client && !this.eCommerceProduct.disableClickAndCollect) {
				this.locationPending = true;
				await useGeolocationStore().requestBestAvailablePosition(true);
				const position = useGeolocationStore().position;
				this.locationPending = false;
				this.$store.dispatch("ecommerce/getStoresWithProduct", {
					gtin: this.$store.state.ecommerce.product.gtin,
					location: position,
				});
			}
		},
		isDisabled(size) {
			if (!this.checkIfColorHasSize(size)) {
				return true;
			}

			return !size.inventory?.[0]?.amount && this.requiresStock;
		},
		checkIfColorHasSize(size) {
			const variantsInSize = this.eCommerceProduct.variants?.filter((variant) => variant.memory === size.memory);
			return variantsInSize.some((variant) => variant.color === this.eCommerceProduct.color);
		},
	},
});
</script>

<style lang="scss" scoped>
.item {
	display: inline-block;
	margin-right: $spacing-s;
	margin-bottom: $spacing-s;
}

.storage-text {
	margin-bottom: $spacing-m;
	color: $color-neutrals-900-shade;
}

.disabled {
	pointer-events: none;
	cursor: pointer;
}

.info-text-container {
	margin-top: $spacing-s;
}

.info-text {
	align-items: center;
	display: flex;
	gap: $spacing-xs;
	color: $color-cta-default;
	text-decoration: underline;
	&:hover {
		cursor: pointer;
	}

	:deep(svg) {
		flex-shrink: 0;
	}

	:deep(p) {
		margin: 0;
	}
}
</style>
